import React, { useEffect, useState } from 'react';
import { Button } from '../Button';
import TextInput from '../TextInput';
import PieChart from './PieChart';
import { parseFullAmount, parseSmallAmount, postHeightToParent, validAmount } from './../common-helpers';
import './tax-burden.css';

const initialState = {
	adjustedGrossIncome: "",
	level: 0,
	hasError: false,
	hasResult: false,
};

export const TaxBurden: React.FC = () => {
	const [ { adjustedGrossIncome, level, hasError, hasResult }, setValues ] = useState(initialState);

	const levels = [
		{ low: 0, high: 44269.00 },
		{ low: 44269.01, high: 87917.00 },
		{ low: 87917.01, high: 154589.00 },
		{ low: 154589.01, high: 221572.00 },
		{ low: 221572.01, high: 546434.00 },
	];
	const incomeLevel = ["44269", "44269", "87917", "154589", "221572", "546434"];
	const percent = ["50", "50", "25", "10", "5", "1"];
	const numberOfTaxpayers = ["74.1", "74.1", "37.1", "14.8", "7.4", "1.5"];
	const moreOrLess = ["less", "more", "more", "more", "more", "more"];
	const tax = ["$48.4","$1.5","$1.4","$1.1","$938","$612"];
	const reported = ["11.5", "88.5", "68.8", "47.3", "35.9", "20.1"];
	const paid = ["3.1", "96.9", "86.7", "70.8", "59.4", "38.8"];
	const avgRate = ["3.54", "14.55", "16.73", "19.89", "21.98", "25.57"];
	const billion = ["billion", "trillion", "trillion", "trillion", "billion", "billion"];

	const onSubmit = () => {
		let agiInt = Number(validAmount(adjustedGrossIncome));

		if (agiInt === 0) { setValues((values) => ({ ...values, hasError: true })); }
		else {
			let level = levels.findIndex((level) => agiInt >= level.low && agiInt <= level.high);
			if (level === -1) { level = 5 };

			setValues((values) => ({ ...values, hasResult: true, level: level }));
		}
	};

	const onReset = () => {
		setValues({ ...initialState });
	};

  useEffect(() => {
    postHeightToParent(document.body.scrollHeight);
  }, [hasError, hasResult]);

	return (
		<div className="container_taxBurden">
			{ hasResult ? (
				<div className="taxBurden-results">
					<div className="taxBurden-results-block">
						<div className="taxBurden-results-section">
							<p>
								Your { adjustedGrossIncome } adjusted gross income (AGI) puts you in the
								<span className="taxBurden-result-highlight">{ level === 0 ? "lowest" : "top" } { percent[level] }% of earners.</span>
							</p>
							<PieChart percent={ Number(percent[level]) } containerId="taxBurden-pie1" text={ level === 0 ? "lowest" : "top" } className="taxBurden-pie-chart" />
						</div>
						<div className="taxBurden-results-section">
							<p>
								The { level === 0 ? "lowest-earning" : "top-earning" } { percent[level] }% of taxpayers reported { reported[level] }% of all AGI and
								<span className="taxBurden-result-highlight">paid { paid[level] }% of total federal income taxes.</span>
							</p>
							<PieChart percent={ Number(paid[level]) } containerId="taxBurden-pie2" text=""  className="taxBurden-pie-chart" />
						</div>
						<div className="">
							<p>
								Together, you and the other { numberOfTaxpayers[level]} million taxpayers with incomes of { parseFullAmount(incomeLevel[level]) } or { moreOrLess[level] }
								<span className="taxBurden-result-highlight">paid a total of { tax[level] } { billion[level] } in federal income taxes.</span>
							</p>
						</div>
					</div>
					<div className="taxBurden-results-block">
						<p>
							The average tax rate paid in 2019 by the { level === 0 ? "lowest" : "top" } { percent[level] }% of earners was
							<span className="taxBurden-result-highlight">{ avgRate[level] }%</span>
						</p>
					</div>

					<Button
						label="Start over"
						className="default-button taxBurden-btn"
						onClickHandler={onReset}
					/>
				</div>
			) : (
			<form>
				<label htmlFor="adjusted-gross-income">Enter your adjusted gross income (AGI) from your most recent federal income tax return to see how your income stacks up and what portion of the tax burden you bear:</label>
				<br/>
				<TextInput
					id="adjusted-gross-income"
					name="adjusted-gross-income"
					fieldClass="taxBurden-input"
					onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
						setValues((values) => ({ ...values, adjustedGrossIncome: parseSmallAmount(e.target.value, 16), hasError: false }));
					}}
					value={adjustedGrossIncome}
					placeholder="$0.00"
					inputMode="numeric"
					data-testid="adjusted-gross-income"
					/>
				<Button
					label="Submit"
					data-testid="taxBurden-btn"
					className="primary-button taxBurden-btn"
					onClickHandler={onSubmit}
				/>
				<p className="taxBurden-description">Your AGI appears on Line 11 of your 2020 or 2021 return. If you don't have your tax return handy, enter your annual salary instead – the IRS categories are broad enough that your result will likely be the same.</p>
				{ hasError && (
					<p className="taxBurden-error"><b>Error:</b> Income must be greater than 0. Please reenter.</p>
				)}
			</form>
			)}
		</div>
	);
};
